<template>
  <v-container>
    <the-dialog-box
      ref="dialogBox"
      :submit="submit"
      :submit-btn-text="'Submit'"
      :max-width="600"
      :btn-loading="loading"
    >
      <template slot="header">
        <span class="headline">Multi-Product QR Edit Form</span>
      </template>
      <template slot="body">
        <v-container v-if="mpqrItem" style="font-size: 1.3em !important;" fluid class="mt-3">
          <v-row class="pa-3" style="background-color: #E8F5E9; border: 2px solid rgba(0,0,0,0.2); border-radius: 4px;">
            <v-col class="text-center" cols="6">
              <h5 class="h5">MPQR Code</h5> 
              <span style="font-size: 1.5em;"> {{ qrCode }}</span>
            </v-col>
            <v-col class="text-center" cols="6">
              <h5 class="h5">Inventory Class</h5>
              <v-chip
                class="mt-1 mb-1"
                text-color="white"
                style="cursor: pointer;"
                color="#8dbf8d"
              >
                <span style="font-size: 20px; color: #fff !important;">PAINT</span>
              </v-chip>
            </v-col>
            <v-col class="pb-0" cols="6">
              <v-text-field :value="inventoryTag.location.name" :disabled="true" label="Location" />
            </v-col>
            <v-col class="pb-0" cols="6">
              <v-text-field :value="inventoryTag.inventory_location.name" :disabled="true" label="Shelf" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <v-autocomplete
                v-model="mpqrItem.project_id"
                clearable
                :items="projects"
                :loading="loadingProject"
                item-text="name"
                item-value="id"
                label="Project"
              />
            </v-col>
            <v-col cols="12" class="pb-0">
              <v-autocomplete
                v-model="mpqrItem.glass_paint_code_id"
                label="Paint Code"
                :items="glassPaintCodes"
                item-value="id"
                :loading="loadingItem"
                :item-text="(item) => item.code + ': ' + item.description"
                :disabled="true"
              >
                <template #selection="{ item }">
                  <v-chip
                    class="mt-1 mb-1 mr-3"
                    small
                    text-color="white"
                    style="cursor: pointer;"
                    color="#8dbf8d"
                  >
                    PAINT
                  </v-chip><span style="font-weight: bold;">{{ item.code }}</span><span>{{ ': ' + item.description }}</span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12">
              <v-text-field label="UOM" :value="mpqrItem.uom.name" :disabled="true" />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="mpqrItem.quantity"
                :rules="[(v) => !!v || 'Quantity is required', rules.wholeAndHalfNumber]"
                type="number"
                min="0"
                :step="0.5"
              >
                <template slot="label">
                  Quantity<RedAsterisk />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </the-dialog-box>
    <confirm-form ref="confirmForm" />
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data: () => ({
    qrCode: null,
    inventoryTag: null,
    mpqrItem: null,
    inventoryClassIdPaint: 12,
    glassPaintCodes: [],
    colors: [],
    projects: [],
    roles: [],
    loadingItem: false,
    loadingColor: false,
    loading: false,
    loadingProject: false,
    uomIdBucket: 9,
    userRoleIdAdmin: 3,

    rules: {
      wholeAndHalfNumber: (v) => Number(v) === Math.floor(Number(v)) || (Number(v) - 0.5) === Math.floor(Number(v)) || 'Enter a whole number or a number ending in .5',
    },
  }),
  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
    isAdmin() {
      return this.roles.includes(this.userRoleIdAdmin);
    },
  },
  created() {
    this.roles = this.user.roles.map((x) => x.id);
    this.getGlassPaintCodes();
    this.getProjects();
  },
  methods: {
    async open(qrCode, mpqrItemId) {
      this.qrCode = qrCode;
      this.$refs.dialogBox.open();
      await this.getInventoryTag(mpqrItemId);
      if ((!this.inventoryTag.id || this.inventoryTag.id === 0) && !this.isAdmin) {
        this.$bus.$emit('showError', 'MPQR creation restricted to admins');
        this.$refs.dialogBox.close(); // opening then closing if restricted is weird UI/UX because the user will see the form for like 1ms but if we don't open it first, then vue won't register the v-models so if you get an existing inventory tag then open the dialog box, the fields won't be populated
        return;
      }
    },

    async getInventoryTag(mpqrItemId) {
      if (!this.qrCode) return;
      this.loading = true;

      const isQtyOnHandZero = 0;

      const submitModel = {
        filters: {
          inventory_tag: this.qrCode,
          is_qty_on_hand_o: isQtyOnHandZero,
        },
      };

      const res = await this.$api.inventoryTag.read(submitModel);
      if (!res || !res.data || !res.data[0]) {
        this.loading = false;
        return;
      }

      const firstIndex = 0;
      this.inventoryTag = res.data[firstIndex];
      this.mpqrItem = this.inventoryTag.multi_products.find((item) => item.id === mpqrItemId);
      this.mpqrItem.po_number = this.inventoryTag.po_number;
      this.loading = false;
    },

    async getGlassPaintCodes() {
      this.loadingItem = true;
      const submitModel = {
        filters: { is_awd_paint: true },
      };
      this.glassPaintCodes = await this.$api.glassPaintCode.read(submitModel);
      this.loadingItem = false;
    },

    async getProjects() {
      this.loadingProject = true;
      const res = await this.$api.project.read({ filters: { is_all: 1 } });
      const cancelCode = 3001;
      if (res === cancelCode) { return; }
      if (!res || !res.data) {
        this.loadingProject = false;
        return;
      }
      this.projects = res.data;
      this.loadingProject = false;
    },

    async submit() {
      this.$bus.$emit('loading');
      this.mpqrItem.inventory_tag = this.qrCode;
      let res = await this.$api.inventoryMPQR.update(this.mpqrItem);

      if (!res) {
        this.$bus.$emit('showError');
        this.$bus.$emit('loading-stop');
        return false;
      }
      if (res == 'exist') {
        // this.$bus.$emit('showError', 'An MPQR already exists with these parameters');
        this.$bus.$emit('loading-stop');
        // return false;
        if (this.mpqrItem.id) {
          const confirm = await this.$refs.confirmForm.open({
            title: 'An MPQR already exists with these parameters, do you want to merge the existing item? This will delete the current item and the quantity will be added to the existing item.',
          });
          if (confirm) {
            this.$bus.$emit('loading');
            this.mpqrItem.merge_existing = 1;
            const updateRes = await this.$api.inventoryMPQR.update(this.mpqrItem);
            if (!updateRes) {
              this.$bus.$emit('showError');
              this.$bus.$emit('loading-stop');
              return false;
            }
          } else {
            return false;
          }
        } else {
          this.$bus.$emit('showError', 'An MPQR already exists with these parameters');
          this.$bus.$emit('loading-stop');
          return false;
        }
      }

      this.$bus.$emit('loading-stop');
      this.$bus.$emit('showSuccess');
      this.$emit('success');
      return res;
    },
  },
};
</script>

<style scoped>
span{
  color: black !important;
}

/* ::v-deep .theme--light.v-input--is-disabled
{
  color: black !important;
} */
</style>
